import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import activityIcon from './assets/activity.svg?react';
import botIcon from './assets/bot.svg?react';
import chatIcon from './assets/chat.svg?react';
import copyIcon from './assets/copy.svg?react';
import discoveryIcon from './assets/discovery.svg?react';
import eventIcon from './assets/event.svg?react';
import graphIcon from './assets/graph.svg?react';
import infoIcon from './assets/info.svg?react';
import noticeIcon from './assets/noti.svg?react';
import paperIcon from './assets/paper.svg?react';
import swapIcon from './assets/swap.svg?react';
import trophyIcon from './assets/trophy.svg?react';
import walletIcon from './assets/wallet.svg?react';
import workIcon from './assets/work.svg?react';
import { IconX } from '@tabler/icons-react';

const BlurOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 999;
`;

const DropdownContainer = styled.div`
  width: 420px;
  height: auto;
  max-height: 480px;
  background: #0d1117;
  border: 1px solid #353535;
  border-radius: 12px;
  display: flex;
  font-family: 'NanumSquareRound', sans-serif;
`;

const LeftPanel = styled.div`
  width: 152px;
  border-right: 1px solid #353535;
  display: flex;
  flex-direction: column;
  padding: 20px 12px;
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 268px;
`;

const RightPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 10px;
  border-bottom: 1px solid #353535;
  height: 44px;
  box-sizing: border-box;
`;

const RightPanelTitle = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  line-height: normal;
  letter-spacing: -0.28px;
`;

const MenuItem = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => (props.$isActive ? '#ffffff' : '#b3b5bd')};
  transition: all 0.2s;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #ffffff;
  }
`;

const CloseButton = styled.button`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  flex-shrink: 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const SubItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubItemCard = styled.button`
  display: flex;
  align-items: start;
  gap: 12px;
  padding: 12px 10px;
  background: transparent;
  border: none;
  border-bottom: 1px solid #353535;
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const SubItemIconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SubItemContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const SubItemTitle = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
`;

const SubItemDescription = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 300;
  color: #b3b5bd;
  line-height: normal;
  letter-spacing: -0.24px;
  text-align: left;
  white-space: normal;
  word-wrap: break-word;
`;

interface MenuItem {
  id: string;
  labelKey: string;
  subItems?: SubItem[];
}

interface SubItem {
  id: string;
  titleKey: string;
  descriptionKey: string;
  icon: React.ElementType;
  route: string;
}

const menuItemsConfig: MenuItem[] = [
  {
    id: 'crypto',
    labelKey: 'Crypto',
    subItems: [
      {
        id: 'buy-sell-crypto',
        titleKey: 'Buy/Sell Crypto',
        descriptionKey: 'menu.buySellCrypto.desc',
        icon: walletIcon,
        route: '/buysell-crypto',
      },
    ],
  },
  {
    id: 'markets',
    labelKey: 'Markets',
    subItems: [
      {
        id: 'markets-overview',
        titleKey: 'Markets Overview',
        descriptionKey: 'menu.marketsOverview.desc',
        icon: graphIcon,
        route: '/markets',
      },
      {
        id: 'fear-greed-index',
        titleKey: 'Fear & Greed Index',
        descriptionKey: 'menu.fearGreedIndex.desc',
        icon: activityIcon,
        route: '/fear-greed-index',
      },
    ],
  },
  {
    id: 'futures',
    labelKey: 'Futures',
    subItems: [
      {
        id: 'usdt-perpetuals',
        titleKey: 'USDT Perpetuals',
        descriptionKey: 'menu.usdtPerpetuals.desc',
        icon: swapIcon,
        route: '/trade/2',
      },
      {
        id: 'demo-trading',
        titleKey: 'demoTrading',
        descriptionKey: 'demoTradingDescription',
        icon: activityIcon,
        route: '/demo-trade/2',
      },
      {
        id: 'copy-trading',
        titleKey: 'Copy Trading',
        descriptionKey: 'menu.copyTrading.desc',
        icon: copyIcon,
        route: '/copy-trading',
      },
      {
        id: 'trading-bots',
        titleKey: 'Trading Bots',
        descriptionKey: 'menu.tradingBots.desc',
        icon: botIcon,
        route: '/trading-bots',
      },
    ],
  },
  {
    id: 'more',
    labelKey: 'More',
    subItems: [
      {
        id: 'about-us',
        titleKey: 'About Us',
        descriptionKey: 'menu.aboutUs.desc',
        icon: infoIcon,
        route: '/about-us',
      },
      {
        id: 'institutional',
        titleKey: 'Institutional',
        descriptionKey: 'menu.institutional.desc',
        icon: workIcon,
        route: '/institutional',
      },
      {
        id: 'terms-conditions',
        titleKey: 'Terms and Conditions',
        descriptionKey: 'menu.termsConditions.desc',
        icon: paperIcon,
        route: '/terms-and-conditions',
      },
      {
        id: 'guide',
        titleKey: 'Guide',
        descriptionKey: 'menu.guide.desc',
        icon: discoveryIcon,
        route: '/guide',
      },
      {
        id: 'notice-center',
        titleKey: 'Notice Center',
        descriptionKey: 'menu.noticeCenter.desc',
        icon: noticeIcon,
        route: '/notice',
      },
    ],
  },
  {
    id: 'event-rewards',
    labelKey: 'Event Rewards',
    subItems: [
      {
        id: 'event-rewards',
        titleKey: 'Event Rewards',
        descriptionKey: 'menu.eventRewards.desc',
        icon: eventIcon,
        route: '/reward',
      },
    ],
  },
  {
    id: 'customer-service',
    labelKey: 'Customer Service',
    subItems: [
      {
        id: 'customer-service',
        titleKey: 'Customer Service',
        descriptionKey: 'menu.customerService.desc',
        icon: chatIcon,
        route: '/support',
      },
    ],
  },
];

interface CategoryMenuDropdownProps {
  onClose: () => void;
  isOpen: boolean;
}

const CategoryMenuDropdown: React.FC<CategoryMenuDropdownProps> = ({
  onClose,
  isOpen,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeMenuId, setActiveMenuId] = useState('crypto');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeMenu = menuItemsConfig.find((item) => item.id === activeMenuId);

  const handleSubItemClick = (route: string) => {
    navigate(route);
    onClose();
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <>
      {createPortal(isOpen && <BlurOverlay onClick={onClose} />, document.body)}
      <DropdownContainer ref={dropdownRef}>
        <LeftPanel>
          {menuItemsConfig.map((item) => (
            <MenuItem
              key={item.id}
              $isActive={activeMenuId === item.id}
              onClick={() => setActiveMenuId(item.id)}
            >
              {t(item.labelKey)}
            </MenuItem>
          ))}
        </LeftPanel>

        <RightPanel>
          <RightPanelHeader>
            <RightPanelTitle>
              {activeMenu ? t(activeMenu.labelKey) : ''}
            </RightPanelTitle>
            <CloseButton onClick={onClose}>
              <IconX size={16} color="#ffffff" />
            </CloseButton>
          </RightPanelHeader>

          {activeMenu &&
            activeMenu.subItems &&
            activeMenu.subItems.length > 0 && (
              <SubItemsContainer>
                {activeMenu.subItems.map((subItem) => {
                  const IconComponent = subItem.icon;
                  return (
                    <SubItemCard
                      key={subItem.id}
                      onClick={() => handleSubItemClick(subItem.route)}
                    >
                      <SubItemIconWrapper>
                        <IconComponent size={24} color="#ffffff" />
                      </SubItemIconWrapper>
                      <SubItemContent>
                        <SubItemTitle>{t(subItem.titleKey)}</SubItemTitle>
                        <SubItemDescription>
                          {t(subItem.descriptionKey)}
                        </SubItemDescription>
                      </SubItemContent>
                    </SubItemCard>
                  );
                })}
              </SubItemsContainer>
            )}
        </RightPanel>
      </DropdownContainer>
    </>
  );
};

export default CategoryMenuDropdown;
